const timeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const dateFmt = new Intl.DateTimeFormat(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
const longFmt = new Intl.DateTimeFormat(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const dayFmt = new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function isSameDay(a: string, b: string): boolean {
  return startOfDay(new Date(a)) === startOfDay(new Date(b));
}

/** "Today at 3:04 PM", "Yesterday at 9:12 AM", or "09/27/2026 3:04 PM". */
export function formatMessageTime(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (days === 0) return `Today at ${timeFmt.format(date)}`;
  if (days === 1) return `Yesterday at ${timeFmt.format(date)}`;
  return `${dateFmt.format(date)} ${timeFmt.format(date)}`;
}

export const formatClock = (iso: string) => timeFmt.format(new Date(iso));

const shortDayFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

/** A moment later on: "3:04 PM" today, "tomorrow at 9:12 AM", or "Fri, Oct 9 at 3:04 PM". */
export function formatLater(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const days = Math.round((startOfDay(date) - startOfDay(now)) / 86_400_000);
  if (days <= 0) return timeFmt.format(date);
  if (days === 1) return `tomorrow at ${timeFmt.format(date)}`;
  return `${shortDayFmt.format(date)} at ${timeFmt.format(date)}`;
}

export const formatLong = (iso: string) => longFmt.format(new Date(iso));
export const formatDay = (iso: string) => dayFmt.format(new Date(iso));

/** Compact relative time: "now", "5m", "3h", "2d", then a date. */
export function formatRelative(iso: string, now = Date.now()): string {
  const diff = Math.max(0, now - new Date(iso).getTime());
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return dateFmt.format(new Date(iso));
}

/** Consecutive messages by one author within this window are grouped under one header. */
export const GROUP_WINDOW_MS = 7 * 60 * 1000;

/** Time remaining until a future date: "in 45m", "in 5h", "in 3d". */
export function formatUntil(iso: string, now = Date.now()): string {
  const minutes = Math.max(0, Math.round((new Date(iso).getTime() - now) / 60_000));
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `in ${hours}h`;
  return `in ${Math.round(hours / 24)}d`;
}

const eventDayFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const eventTimeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "Today at 7:00 PM – 9:00 PM", "Tomorrow at 8:00 PM", "Sat, Oct 10 at 7:00 PM". */
export function formatEventTime(event: { startsAt: string; endsAt: string | null }, now = new Date()): string {
  const start = new Date(event.startsAt);
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const day = sameDay(start, now) ? 'Today' : sameDay(start, tomorrow) ? 'Tomorrow' : eventDayFmt.format(start);
  const end = event.endsAt ? new Date(event.endsAt) : null;
  const endText = end ? ` – ${sameDay(start, end) ? eventTimeFmt.format(end) : `${eventDayFmt.format(end)}, ${eventTimeFmt.format(end)}`}` : '';
  return `${day} at ${eventTimeFmt.format(start)}${endText}`;
}
